import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ExternalLink, Layers3 } from 'lucide-react'
import { Link, useSearchParams } from 'react-router'
import type { CollectionKeywordSummary, CollectionMetric } from '@/api/collectionKeywords/types'
import type { KeywordAssignment, KeywordType } from '@/api/keywordAnalysis/types'
import { CollectionKeywordNav } from '@/features/collectionKeywords/CollectionKeywordNav'
import {
  useCollectionKeywords,
  useCollectionKeywordSummaries,
} from '@/features/collectionKeywords/useCollectionKeywords'

export type CollectionKeywordsView = 'analysis' | 'coverage' | 'traffic'
type SortColumn = 'sales' | CollectionMetric
type Direction = 'asc' | 'desc'
const pageSizes = [10, 25, 50, 100, 500] as const
type PageSize = (typeof pageSizes)[number]
const formatter = new Intl.NumberFormat('de-DE')

const keywordGroups = [
  {
    type: 0,
    label: 'Primär',
    color: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40',
  },
  {
    type: 1,
    label: 'Sekundär',
    color: 'border-teal-200 bg-teal-50 dark:border-teal-900 dark:bg-teal-950/40',
  },
  {
    type: 2,
    label: 'Emotion',
    color: 'border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40',
  },
  {
    type: 3,
    label: 'Longtail',
    color: 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40',
  },
] as const satisfies readonly {
  readonly type: KeywordType
  readonly label: string
  readonly color: string
}[]

function parsePage(value: string | null): number {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Die Collection-Daten konnten nicht geladen werden.'
}

function parsePageSize(value: string | null): PageSize {
  const parsed = Number(value)
  return pageSizes.find((size) => size === parsed) ?? 25
}

function parseSort(value: string | null): SortColumn {
  return ['total', 'primary', 'secondary', 'emotion', 'longtail', 'alias'].includes(value ?? '')
    ? (value as CollectionMetric)
    : 'sales'
}

function metric(summary: CollectionKeywordSummary, view: CollectionKeywordsView, sort: SortColumn) {
  if (sort === 'sales') return summary.sales365
  const suffix = view === 'traffic' ? 'Traffic' : 'Count'
  const key = `${sort}${suffix}` as keyof CollectionKeywordSummary
  const value = summary[key]
  return typeof value === 'number' ? value : 0
}

function SummaryPagination({
  page,
  pageSize,
  totalPages,
  onPage,
  onPageSize,
}: {
  readonly page: number
  readonly pageSize: PageSize
  readonly totalPages: number
  readonly onPage: (page: number) => void
  readonly onPageSize: (size: PageSize) => void
}) {
  return (
    <nav className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => {
          onPage(page - 1)
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-40 dark:border-slate-700"
      >
        <ChevronLeft size={16} /> Zurück
      </button>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
        Pro Seite
        <select
          value={pageSize}
          onChange={(event) => {
            onPageSize(parsePageSize(event.target.value))
          }}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 font-bold dark:border-slate-700 dark:bg-slate-800"
        >
          {pageSizes.map((size) => (
            <option key={size}>{size}</option>
          ))}
        </select>
        <span>
          Seite {page} von {totalPages}
        </span>
      </label>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => {
          onPage(page + 1)
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-40 dark:border-slate-700"
      >
        Weiter <ChevronRight size={16} />
      </button>
    </nav>
  )
}

export function CollectionCard({
  summary,
  keywords,
}: {
  readonly summary: CollectionKeywordSummary
  readonly keywords: readonly KeywordAssignment[]
}) {
  const aliases = keywords.filter((keyword) => keyword.isAlias)
  const editUrl = `https://panda-weltmaschine.de/product-data/collections?init_id=${String(summary.collectionId)}`
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-lg bg-fuchsia-700 px-2.5 py-1.5 font-mono text-xs font-black text-white">
              #{summary.collectionId}
            </span>
            <h2 className="font-black text-slate-950 dark:text-white">{summary.collectionName}</h2>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              {summary.parentName === null
                ? 'Haupt-Collection'
                : `Unter-Collection von ${summary.parentName}`}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {keywords.length} Keyword-Zuordnungen · {formatter.format(summary.sales365)} Verkäufe
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled
            className="cursor-not-allowed rounded-xl bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-400 dark:bg-violet-950/40"
          >
            Neue Keywords anfordern
          </button>
          <a
            href={editUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold dark:border-slate-700"
          >
            In Weltmaschine bearbeiten <ExternalLink size={14} />
          </a>
        </div>
      </header>
      <div className="grid gap-3 p-4 lg:grid-cols-2">
        {keywordGroups.map((group) => {
          const rows = keywords.filter((keyword) => keyword.keywordType === group.type)
          return (
            <KeywordBox key={group.type} label={group.label} color={group.color} keywords={rows} />
          )
        })}
        <KeywordBox
          label="Alias"
          color="border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/40 lg:col-span-2"
          keywords={aliases}
        />
      </div>
    </article>
  )
}

function KeywordBox({
  label,
  color,
  keywords,
}: {
  readonly label: string
  readonly color: string
  readonly keywords: readonly KeywordAssignment[]
}) {
  const traffic = keywords.reduce((sum, keyword) => sum + (keyword.searchVolumePerMonthAvg ?? 0), 0)
  return (
    <section className={`rounded-xl border p-4 ${color}`}>
      <div className="mb-3 flex justify-between gap-2">
        <h3 className="text-xs font-black uppercase tracking-wider">{label}</h3>
        <span className="text-[10px] font-bold uppercase text-slate-500">
          {keywords.length} · {formatter.format(traffic)} Traffic
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {keywords.length === 0 ? (
          <span className="text-sm text-slate-400">Noch keine Keywords</span>
        ) : (
          keywords.map((keyword) => (
            <span
              key={keyword.keywordId}
              className="rounded-full border border-white bg-white px-3 py-1.5 text-sm font-medium shadow-sm dark:border-slate-700 dark:bg-slate-800"
            >
              {keyword.value}{' '}
              <small className="text-slate-400">
                {formatter.format(keyword.searchVolumePerMonthAvg ?? 0)}
              </small>
            </span>
          ))
        )}
      </div>
    </section>
  )
}

function SummaryTable({
  rows,
  view,
  sort,
  direction,
  showDetails,
  onSort,
}: {
  readonly rows: readonly CollectionKeywordSummary[]
  readonly view: 'coverage' | 'traffic'
  readonly sort: SortColumn
  readonly direction: Direction
  readonly showDetails: boolean
  readonly onSort: (column: SortColumn) => void
}) {
  const columns = ['total', 'primary', 'secondary', 'emotion', 'longtail', 'alias'] as const
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-100 text-xs uppercase tracking-wider dark:bg-slate-800">
          <tr>
            <th className="px-4 py-3 text-left">Collection</th>
            {showDetails ? (
              <>
                <th className="min-w-80 px-4 py-3 text-left">Beschreibung</th>
                <th className="min-w-80 px-4 py-3 text-left">AI Instructions</th>
              </>
            ) : null}
            <SortHead
              label="Verkäufe"
              column="sales"
              sort={sort}
              direction={direction}
              onSort={onSort}
            />
            {columns.map((column) => (
              <SortHead
                key={column}
                label={
                  {
                    total: 'Gesamt',
                    primary: 'Primär',
                    secondary: 'Sekundär',
                    emotion: 'Emotion',
                    longtail: 'Longtail',
                    alias: 'Alias',
                  }[column]
                }
                column={column}
                sort={sort}
                direction={direction}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <SummaryRow key={row.collectionId} row={row} view={view} showDetails={showDetails} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SortHead({
  label,
  column,
  sort,
  direction,
  onSort,
}: {
  readonly label: string
  readonly column: SortColumn
  readonly sort: SortColumn
  readonly direction: Direction
  readonly onSort: (column: SortColumn) => void
}) {
  return (
    <th className="px-4 py-3 text-center">
      <button
        type="button"
        onClick={() => {
          onSort(column)
        }}
        className="inline-flex items-center gap-1"
      >
        {label}
        {sort === column ? (
          direction === 'asc' ? (
            <ArrowUp size={13} />
          ) : (
            <ArrowDown size={13} />
          )
        ) : null}
      </button>
    </th>
  )
}

function SummaryRow({
  row,
  view,
  showDetails,
}: {
  readonly row: CollectionKeywordSummary
  readonly view: 'coverage' | 'traffic'
  readonly showDetails: boolean
}) {
  const editUrl = `https://panda-weltmaschine.de/product-data/collections?init_id=${String(row.collectionId)}`
  const values =
    view === 'coverage'
      ? [
          row.totalCount,
          row.primaryCount,
          row.secondaryCount,
          row.emotionCount,
          row.longtailCount,
          row.aliasCount,
        ]
      : [
          row.totalTraffic,
          row.primaryTraffic,
          row.secondaryTraffic,
          row.emotionTraffic,
          row.longtailTraffic,
          row.aliasTraffic,
        ]
  return (
    <tr className="border-t border-slate-200 dark:border-slate-700">
      <td className="px-4 py-3">
        <div className="min-w-56">
          <Link
            to={`/collection-keywords/collection/${String(row.collectionId)}`}
            className="font-bold text-slate-950 hover:text-fuchsia-700 dark:text-white"
          >
            #{row.collectionId} {row.collectionName}
          </Link>
          <p className="text-xs text-slate-400">{row.parentName ?? 'Haupt-Collection'}</p>
          <a
            href={editUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-slate-500 hover:text-blue-700"
          >
            Weltmaschine ↗
          </a>
        </div>
      </td>
      {showDetails ? (
        <>
          <td className="max-w-md px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
            {row.description === null || row.description.trim() === ''
              ? 'Nicht gepflegt'
              : row.description}
          </td>
          <td className="max-w-md px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
            {row.aiInstructions === null || row.aiInstructions.trim() === ''
              ? 'Nicht gepflegt'
              : row.aiInstructions}
          </td>
        </>
      ) : null}
      <td className="px-4 py-3 text-center font-black text-emerald-700">
        {formatter.format(row.sales365)}
      </td>
      {values.map((value, index) => (
        <td key={index} className="px-4 py-3 text-center">
          <span
            className={
              value === 0
                ? 'rounded-full bg-red-100 px-2 py-1 text-xs font-bold text-red-700'
                : 'font-bold tabular-nums'
            }
          >
            {value === 0 ? 'Fehlt' : formatter.format(value)}
          </span>
          {view === 'traffic' ? (
            <small className="ml-1 text-slate-400">
              (
              {
                [
                  row.totalCount,
                  row.primaryCount,
                  row.secondaryCount,
                  row.emotionCount,
                  row.longtailCount,
                  row.aliasCount,
                ][index]
              }
              )
            </small>
          ) : null}
        </td>
      ))}
    </tr>
  )
}

export function CollectionKeywordsPage({
  view,
}: {
  readonly view: CollectionKeywordsView
}): ReactNode {
  const [params, setParams] = useSearchParams()
  const page = parsePage(params.get('page'))
  const pageSize = parsePageSize(params.get('limit'))
  const sort = parseSort(params.get('sort'))
  const direction: Direction =
    params.get('direction') === 'desc' ? 'desc' : sort === 'sales' ? 'desc' : 'asc'
  const showDetails = params.get('details') === '1'
  const summariesQuery = useCollectionKeywordSummaries()
  const ranked = [...(summariesQuery.data ?? [])].sort((a, b) => {
    const diff = metric(a, view, sort) - metric(b, view, sort)
    if (diff !== 0) return direction === 'asc' ? diff : -diff
    return b.sales365 - a.sales365
  })
  const totalPages = Math.max(1, Math.ceil(ranked.length / pageSize))
  const visible = ranked.slice((page - 1) * pageSize, page * pageSize)
  const ids = visible.map((row) => row.collectionId)
  const keywordsQuery = useCollectionKeywords(view === 'analysis' ? ids : [])
  const keywordsById = new Map<number, KeywordAssignment[]>()
  for (const row of keywordsQuery.data ?? []) {
    const list = keywordsById.get(row.collectionId) ?? []
    list.push(row.keyword)
    keywordsById.set(row.collectionId, list)
  }
  const write = (
    nextPage: number,
    nextSize: PageSize,
    nextSort: SortColumn,
    nextDirection: Direction,
    nextDetails: boolean,
  ) => {
    const p = new URLSearchParams()
    if (nextPage > 1) p.set('page', String(nextPage))
    if (nextSize !== 25) p.set('limit', String(nextSize))
    if (nextSort !== 'sales') p.set('sort', nextSort)
    if (nextDirection !== (nextSort === 'sales' ? 'desc' : 'asc')) p.set('direction', nextDirection)
    if (nextDetails) p.set('details', '1')
    setParams(p)
  }
  const onSort = (column: SortColumn): void => {
    write(
      1,
      pageSize,
      column,
      column === sort
        ? direction === 'asc'
          ? 'desc'
          : 'asc'
        : column === 'sales'
          ? 'desc'
          : 'asc',
      showDetails,
    )
  }
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <CollectionKeywordNav />
      <section className="rounded-3xl bg-gradient-to-br from-slate-950 via-fuchsia-950 to-purple-950 px-7 py-8 text-white">
        <div className="flex items-center gap-2 text-fuchsia-200">
          <Layers3 size={18} /> Collection-Keywords
        </div>
        <h1 className="mt-3 text-3xl font-black">
          {view === 'analysis'
            ? 'Collection Keyword-Analyse'
            : view === 'coverage'
              ? 'Collection Keyword-Abdeckung'
              : 'Collection Keyword-Traffic'}
        </h1>
        <p className="mt-2 max-w-3xl text-slate-300">
          Collections nutzen numerische IDs und können als Haupt- oder Unter-Collection organisiert
          sein. Verkäufe werden aus den zugeordneten Motiven summiert.
        </p>
      </section>
      <div className="mt-6">
        <SummaryPagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPage={(p) => {
            write(p, pageSize, sort, direction, showDetails)
          }}
          onPageSize={(s) => {
            write(1, s, sort, direction, showDetails)
          }}
        />
      </div>
      {view !== 'analysis' ? (
        <div className="mt-3 flex justify-end">
          <label className="text-sm font-semibold">
            Spalten{' '}
            <select
              value={showDetails ? 'details' : 'compact'}
              onChange={(e) => {
                write(1, pageSize, sort, direction, e.target.value === 'details')
              }}
              className="ml-2 rounded-lg border px-3 py-2 dark:bg-slate-800"
            >
              <option value="compact">Kompakt</option>
              <option value="details">Mit Beschreibung und AI Instructions</option>
            </select>
          </label>
        </div>
      ) : null}
      <div className="mt-4">
        {summariesQuery.isLoading ? (
          <div className="h-72 animate-pulse rounded-2xl bg-white dark:bg-slate-900" />
        ) : summariesQuery.error !== null ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
            <p className="font-bold">Die Collection-Kennzahlen konnten nicht geladen werden.</p>
            <p className="mt-2 text-sm">{errorMessage(summariesQuery.error)}</p>
            <button
              type="button"
              onClick={() => {
                void summariesQuery.refetch()
              }}
              className="mt-4 rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold dark:border-red-800"
            >
              Erneut laden
            </button>
          </div>
        ) : view === 'analysis' && keywordsQuery.error !== null ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
            <p className="font-bold">Die Collection-Keywords konnten nicht geladen werden.</p>
            <p className="mt-2 text-sm">{errorMessage(keywordsQuery.error)}</p>
            <button
              type="button"
              onClick={() => {
                void keywordsQuery.refetch()
              }}
              className="mt-4 rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold dark:border-red-800"
            >
              Erneut laden
            </button>
          </div>
        ) : view === 'analysis' ? (
          <div className="space-y-4">
            {visible.map((summary) => (
              <CollectionCard
                key={summary.collectionId}
                summary={summary}
                keywords={keywordsById.get(summary.collectionId) ?? []}
              />
            ))}
          </div>
        ) : (
          <SummaryTable
            rows={visible}
            view={view}
            sort={sort}
            direction={direction}
            showDetails={showDetails}
            onSort={onSort}
          />
        )}
      </div>
      <div className="mt-7">
        <SummaryPagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPage={(p) => {
            write(p, pageSize, sort, direction, showDetails)
          }}
          onPageSize={(s) => {
            write(1, s, sort, direction, showDetails)
          }}
        />
      </div>
    </div>
  )
}
